import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Cookies from 'js-cookie';
import { useRef, useState } from 'react';
import { fetchAPI } from '../utils';
import { ACTION_SUBJECT_IDS } from '../../utils/constants';
const formatPrice = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    style: 'currency',
    currency: 'USD',
    currencyDisplay: 'code'
}).format;
const useEntitlementListings = (entitlements) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const hasRun = useRef(false);
    const [isFetching, setIsFetching] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [formattedTotalPrice, setFormattedTotalPrice] = useState('');
    const [estimates, setEstimates] = useState([]);
    const fetchEstimates = () => {
        // only fetch (and only once) when we receive the entitlements
        if (entitlements.length === 0) {
            return;
        }
        if (hasRun.current === true) {
            return;
        }
        hasRun.current = true;
        let totalPrice = 0;
        const compiledEstimates = [];
        const entitlementById = {};
        for (const entitlement of entitlements) {
            entitlementById[entitlement.entitlementId] = entitlement;
        }
        void (async () => {
            try {
                setIsFetching(true);
                const allEstimateResponses = await Promise.all(entitlements.map(({ entitlementId }) => fetchAPI('/ccp/api/v1/bill/estimate?entitlementId=' + entitlementId)));
                for (const estimateResp of allEstimateResponses) {
                    const { ok, status, statusText } = estimateResp;
                    if (!ok) {
                        console.error(`Call to /estimate for failed with error: ${status} ${statusText}`);
                        createAnalyticsEvent({
                            action: 'failed',
                            actionSubject: 'Call to /estimate',
                            actionSubjectId: ACTION_SUBJECT_IDS.wacCartEstimateCallFailed,
                            attributes: {
                                aaid: Cookies.get('__aid_user_id'),
                                error: `${status} for ccp entitlement id: ${estimateResp.url.split('entitlementId=')[1]}`
                            }
                        }).fire();
                        continue;
                    }
                    const { entitlementId, items } = (await estimateResp.json());
                    if (!items || items.length === 0) {
                        console.error("Call to /estimate failed, 'items' were null or empty in response.");
                        createAnalyticsEvent({
                            action: 'failed',
                            actionSubject: 'Call to /estimate',
                            actionSubjectId: ACTION_SUBJECT_IDS.wacCartEstimateCallFailed,
                            attributes: {
                                aaid: Cookies.get('__aid_user_id'),
                                error: "'items' were null or empty in response.",
                                entitlementId
                            }
                        }).fire();
                        continue;
                    }
                    const item = items[0];
                    if (!item) {
                        return;
                    }
                    const { chargeQuantity, total } = item;
                    totalPrice += total;
                    const { chargeElement, quantity } = chargeQuantity;
                    if (!quantity ||
                        Number.isNaN(parseInt(quantity)) ||
                        parseInt(quantity) === 0) {
                        createAnalyticsEvent({
                            action: 'failed',
                            actionSubject: 'Call to /estimate',
                            actionSubjectId: ACTION_SUBJECT_IDS.wacCartEstimateCallFailed,
                            attributes: {
                                aaid: Cookies.get('__aid_user_id'),
                                error: '/estimate returned entitlement with zero users',
                                entitlementId
                            }
                        }).fire();
                    }
                    const entitlement = entitlementById[entitlementId];
                    if (entitlement) {
                        const { siteName, productAndPlanName, productKey, daysLeftInTrial } = entitlement;
                        const areDefined = [siteName, productAndPlanName].every((v) => v !== undefined);
                        if (areDefined) {
                            const estimate = {
                                entitlementId,
                                siteName,
                                productAndPlanName,
                                productKey,
                                daysLeftInTrial,
                                formattedTotalPrice: formatPrice(total),
                                usersPriceBreakdown: `${quantity} ${chargeElement}${parseInt(quantity) === 1 ? '' : 's'} x $${total / parseInt(quantity)} each`
                            };
                            compiledEstimates.push(estimate);
                        }
                    }
                }
                setHasError(false);
            }
            catch (error) {
                createAnalyticsEvent({
                    action: 'failed',
                    actionSubject: 'useEntitlementListings hook',
                    actionSubjectId: ACTION_SUBJECT_IDS.wacCartUseEntitlementListingsFailed,
                    attributes: {
                        aaid: Cookies.get('__aid_user_id'),
                        error: error instanceof Error ? error.message : JSON.stringify(error)
                    }
                }).fire();
                setHasError(true);
            }
            setIsFetching(false);
            setFormattedTotalPrice(formatPrice(totalPrice));
            setEstimates(compiledEstimates);
        })();
    };
    return [formattedTotalPrice, estimates, fetchEstimates, isFetching, hasError];
};
export { useEntitlementListings };
//# sourceMappingURL=use-entitlement-listings.js.map